import * as React from 'react';
import {createFileRoute, Outlet, useParams} from '@tanstack/react-router'
import { useSuspenseQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { LoadingScreen } from '@/web/components/molecules/LoadingScreen'
import { CategoriesList } from '@/web/components/molecules/CategoriesList'
import { CategoryFormModal } from '@/web/components/molecules/CategoryFormModal'
import { addCategory, deleteCategory, updateCategory } from '@/web/lib/services'
import type { Category, CategoryInput } from '@/types';
import { categoriesQueryOptions } from '@/web/lib/queryOptions';

export const Route = createFileRoute('/categories/$categoryId')({
  loader: async ({ params, context }) => {
  return context.queryClient?.ensureQueryData(categoriesQueryOptions(params.categoryId));
  },
  preload: true,
  component: RouteComponent,
  pendingComponent: LoadingScreen,
});

const CATEGORY_INPUT_DEFAULT = {
  name: '',
  imageUrl: '',
  parentId: null,
  description: '',
} as CategoryInput;

function RouteComponent() {
  const params = useParams({ from: '/categories/$categoryId' });
  const [isOpen, setIsOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'create' | 'edit'>('create');
  const [category, setCategory] = React.useState<CategoryInput>(CATEGORY_INPUT_DEFAULT);
  const queryClient = useQueryClient();

  const addCategoryMutation = useMutation({
    mutationFn: addCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(categoriesQueryOptions(params.categoryId));
      setIsOpen(false);
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: updateCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(categoriesQueryOptions(params.categoryId));
      setIsOpen(false);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(categoriesQueryOptions(params.categoryId));
      setIsOpen(false);
    },
  });

  const handleAddCategory = () => {
    setMode('create');
    setCategory({
      ...CATEGORY_INPUT_DEFAULT,
      parentId: params.categoryId,
    });
    setIsOpen(true);
  }

  const handleEditCategory = (category: Category | null) => () => {
    setMode('edit');
    setCategory({
      uid: category?.uid || null,
      name: category?.name || '',
      imageUrl: category?.imageUrl || '',
    });
    setIsOpen(true);
  }

  const handleDeleteCategory = (categoryUid: number) => {
      deleteCategoryMutation.mutate(categoryUid);
  }

  const handleSubmit = (data: any) => {
    const categoryData = {
      ...category,
      ...data,
    }
    if(mode === 'create') {
      console.log('creating...');
      addCategoryMutation.mutate(categoryData);
    } else {
      updateCategoryMutation.mutate({
        uid: category?.uid || 0,
        categoryInput: data,
      });
    }
  }

  const { error, data } = useSuspenseQuery(categoriesQueryOptions(params.categoryId));
  if(data) {
    return (<div>
      <CategoryFormModal
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        mode={mode}
        onSubmit={handleSubmit}
        onDelete={handleDeleteCategory}
        category={category}
      />
      <CategoriesList onEditCategory={handleEditCategory} onAddCategory={handleAddCategory} category={data} />
    </div>)
  }
  return null;
}
